import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Text, {
  TEXT_ALIGN,
  TEXT_COLOR,
  TEXT_FONT_WEIGHT,
  TEXT_SEGMENT,
  TEXT_SIZE,
} from "@/bit/components/components.text";
import CustomLink from "@/bit/components/components.custom-link";
import Icon from "@/bit/components/components.icon-glyph";

export const COMPONENT_NAME = "TextBlock";
export { TEXT_COLOR, TEXT_SEGMENT };

/**
 * Componente que permite crear una lista desplegable de elementos.
 */
const TextBlock = (props) => {
  const { defaultExpanded, cmsHandler, cms, visible, blockName } = props;
  const itemsList = props["cms-ml-single-text-block-list"];
  const contentVisible = visible === true || visible === "true";
  const [showAll, setShowAll] = useState(defaultExpanded === "true" ? true : false);
  const isFirstRender = useRef(true);
  const containerRef = useRef(null);

  const taggingInfo = {
    componentName: COMPONENT_NAME,
    componentContent: "",
    componentProduct: "",
    componentVariation: "none",
    eventType: "click",
  };

  useEffect(() => {
    updateLinkFocusability();
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, [showAll]);

  const toggleShowAll = () => {
    setShowAll(value => !value);
  }

  const focusFirstVisibleFocusableElement = () => {
    const firstFocusable = containerRef.current.querySelector('a');
    if (firstFocusable && firstFocusable.tabIndex !== -1) {
      firstFocusable.focus();
    }
  }

  //Mejora de accesibilidad para que solo sean focusables si están en un area visible
  const updateLinkFocusability = () => {
    const container = containerRef.current;
    const links = container.querySelectorAll('a');
    const containerRect = container.getBoundingClientRect();

    links.forEach(link => {
      const linkRect = link.getBoundingClientRect();
      const isVisible = (
        linkRect.top >= containerRect.top &&
        linkRect.bottom <= containerRect.bottom
      );
      link.tabIndex = isVisible ? 0 : -1;
    });
    if (!isFirstRender.current) {
      focusFirstVisibleFocusableElement();
    }
  }


  return (
    <>
      {cmsHandler && (
        <span
          className={"element-editable block"}
          onClick={cms && cmsHandler ?
            () => cmsHandler({
              ...props,
              name: 'cms-block.text-block'
            }) : null}
        >
          <Icon id="lapiz-m" height={15} color="white" />
        </span>
      )}
      {contentVisible && Array.isArray(itemsList) && itemsList.length > 0 && (
        <section id={blockName || ""} className={`text-block container`}>
          <div className={`text-block-items ${showAll ? '' : 'collapse'}`} ref={containerRef}>
            {itemsList.map((item, index) => (
              <Text
                key={index}
                {...item["cms-atom-text-1"]}
                cmsHandler={cmsHandler}
                className="text-block-item" />
            ))}
          </div>

          <CustomLink
            id="toggle-button"
            title={showAll ? "Ver menos" : "Ver más"}
            ariaLabel={showAll ? "Ver menos" : "Ver más"}
            taggingInfo={{
              ...taggingInfo,
              componentProduct: `listItems`,
              componentContent: showAll
                ? "list-plegar"
                : "list-desplegar",
              componentVariation: "none",
              eventType: "click",
            }}
            onClick={toggleShowAll}
          >
            <Text
              text={showAll ? "Ver menos" : "Ver más"}
              align={TEXT_ALIGN.LEFT}
              color={TEXT_COLOR.BLUEA}
              size={TEXT_SIZE.SUBTITLE2}
              fontType={TEXT_FONT_WEIGHT.REGULAR}
              className="text-toggle"
            />
          </CustomLink>
        </section >
      )}
      <style jsx>{`.text-block {
  background-color: #ffffff;
  :global(.text-block-item) {
    margin: 0.5rem 0.2rem;
  }
  :global(.text-toggle) {
    margin: 0.5rem 0.2rem;
  }
  .text-block-item {
    &--with-borders {
      border: 1px solid #eeeeee;
    }

    &--with-bottom-border {
      border-bottom: 1px solid #eeeeee;

      &:hover {
        background-color: #f5f5f5;
      }
    }
  }

  .text-block-items.collapse {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    @media (max-width: 480px) {
      -webkit-line-clamp: 10;
    }
    overflow: hidden;
    margin-bottom: 1rem;
  }

  .item-description {
    :global(span p) {
      margin-bottom: 0.5rem;
    }
    @media (max-width: 480px) {
      :global(span p:nth-last-child(1)) {
        margin-bottom: 1rem;
      }
    }
  }

  :global(.item-title) {
    margin-bottom: 0.5rem;
  }
}
`}</style>
    </>
  );
};

TextBlock.propTypes = {
  /**
   * {
   *  "info": "Visibilidad del bloque (con eliminación del DOM)",
   *  "kind": "both",
   *  "beautifulName": "Visibilidad del bloque"
   * }
   */
  visible: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
};

TextBlock.defaultProps = {
  visible: true
};

export default TextBlock;